import { parseNonNegativeNumber, parsePositiveNumber } from "./serviceOrderProducts";

/** Bag típico de soja: 5.000.000 sementes. */
export const DEFAULT_SEEDS_PER_PACK = 5000000;
export const DEFAULT_SEED_BAG_MILLION = 5;
export const DEFAULT_OPERATOR_SUPPLY_UNIT = "bags";

export const OPERATOR_SUPPLY_UNITS = [
  { value: "seeds", label: "Sementes" },
  { value: "kg", label: "Quilos" },
  { value: "sacks", label: "Sacas de semente" },
  { value: "bags", label: "Bag de semente" },
];

export function normalizeOperatorSupplyUnit(value) {
  const raw = String(value || "")
    .trim()
    .toLowerCase();
  if (raw === "seeds" || raw === "sementes") return "seeds";
  if (raw === "kg" || raw === "quilos" || raw === "kilos") return "kg";
  if (raw === "sacks" || raw === "sacas") return "sacks";
  if (raw === "bags" || raw === "bag") return "bags";
  return "";
}

export function parseIntegerDigits(value) {
  const digits = String(value || "").replace(/\D/g, "");
  if (!digits) return 0;
  const parsed = Number(digits);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

export function formatIntegerPtBr(value) {
  const parsed = parseIntegerDigits(value);
  return parsed ? parsed.toLocaleString("pt-BR") : "";
}

export function operatorSupplyUnitLabel(unit, translate) {
  const t = typeof translate === "function" ? translate : (value) => value;
  switch (normalizeOperatorSupplyUnit(unit)) {
    case "seeds":
      return t("Sementes");
    case "kg":
      return t("Quilos");
    case "sacks":
      return t("Sacas de semente");
    case "bags":
      return t("Bag de semente");
    default:
      return "";
  }
}

function seedsThousandFromRaw(raw) {
  const thousand = parsePositiveNumber(raw && raw.seeds_thousand_per_ha);
  if (thousand) return thousand;
  const million = parsePositiveNumber(raw && raw.seeds_million_per_ha);
  return million ? million * 1000 : 0;
}

function seedsPerPackFromRaw(raw) {
  const units = parseIntegerDigits(raw && raw.seeds_per_pack);
  if (units) return Math.round(units);
  const million =
    parsePositiveNumber(raw && raw.seeds_per_pack_million) ||
    parsePositiveNumber(raw && raw.bag_size_million);
  if (!million) return 0;
  if (million >= 1000) return Math.round(million);
  return Math.round(million * 1000000);
}

/** sementes/m = (mil sementes/ha × 1.000 × espaçamento_m) / 10.000 */
export function computeSeedsPerMeter({ seedsThousandPerHa, rowSpacingCm }) {
  const thousand = parsePositiveNumber(seedsThousandPerHa);
  const spacingCm = parsePositiveNumber(rowSpacingCm);
  if (!thousand || !spacingCm) return 0;
  const spacingM = spacingCm / 100;
  return (thousand * 1000 * spacingM) / 10000;
}

export function computeSeedBags({ supplySeeds, seedsPerPack }) {
  const supply = parsePositiveNumber(supplySeeds);
  const pack = parsePositiveNumber(seedsPerPack);
  if (!supply || !pack) return 0;
  return supply / pack;
}

export function plantingFromPlan(plan) {
  const raw = plan && plan.planting;
  if (!raw || typeof raw !== "object") return null;
  const cultivar = String(raw.cultivar || "").trim();
  const seedsThousandPerHa = seedsThousandFromRaw(raw);
  const rowSpacingCm = parsePositiveNumber(raw.row_spacing_cm);
  if (!cultivar && !seedsThousandPerHa && !rowSpacingCm) return null;
  const seedsPerPack = seedsPerPackFromRaw(raw);
  const seedSupplyMillion = parsePositiveNumber(raw.seed_supply_million);
  const seedSupplySeeds =
    parsePositiveNumber(raw.seed_supply_seeds) ||
    (seedSupplyMillion ? Math.round(seedSupplyMillion * 1000000) : 0);
  return {
    cultivar,
    seedsThousandPerHa,
    rowSpacingCm,
    pmsG: parsePositiveNumber(raw.pms_g),
    seedsPerPack,
    seedSupplySeeds,
    operatorSupplyUnit:
      normalizeOperatorSupplyUnit(raw.operator_supply_unit) ||
      DEFAULT_OPERATOR_SUPPLY_UNIT,
    fertilizerKgHa: parseNonNegativeNumber(raw.fertilizer_kg_ha),
    inoculantWaterLHa: parseNonNegativeNumber(raw.inoculant_water_l_ha),
    binderRecommendation: String(raw.binder_recommendation || "").trim(),
    seedsPerMeter: parsePositiveNumber(raw.seeds_per_meter),
    bagsRequired: parsePositiveNumber(raw.bags_required),
  };
}

export function buildPlantingPayload({
  cultivar,
  seedsThousandPerHa,
  rowSpacingCm,
  pmsG,
  seedsPerPack,
  operatorSupplyUnit,
  fertilizerKgHa,
  inoculantWaterLHa,
  binderRecommendation,
}) {
  const name = String(cultivar || "").trim();
  const thousand = parsePositiveNumber(seedsThousandPerHa);
  const spacing = parsePositiveNumber(rowSpacingCm);
  const pms = parsePositiveNumber(pmsG);
  const pack = Math.round(parseIntegerDigits(seedsPerPack));
  const supplyUnit =
    normalizeOperatorSupplyUnit(operatorSupplyUnit) ||
    DEFAULT_OPERATOR_SUPPLY_UNIT;
  const fertilizer = parseNonNegativeNumber(fertilizerKgHa);
  const inoculant = parseNonNegativeNumber(inoculantWaterLHa);
  const binder = String(binderRecommendation || "").trim();

  if (
    !name &&
    !thousand &&
    !spacing &&
    !pms &&
    !pack &&
    !fertilizer &&
    !inoculant &&
    !binder
  ) {
    return null;
  }

  const seedsPerMeter = computeSeedsPerMeter({
    seedsThousandPerHa: thousand,
    rowSpacingCm: spacing,
  });

  return {
    cultivar: name,
    seeds_thousand_per_ha: thousand,
    seeds_million_per_ha: thousand ? thousand / 1000 : 0,
    row_spacing_cm: spacing,
    pms_g: pms,
    seeds_per_pack: pack,
    seeds_per_pack_million: pack ? pack / 1000000 : 0,
    bag_size_million: pack ? pack / 1000000 : 0,
    operator_supply_unit: supplyUnit,
    fertilizer_kg_ha: fertilizer,
    inoculant_water_l_ha: inoculant,
    binder_recommendation: binder,
    seeds_per_meter: seedsPerMeter,
  };
}

export function formatPlantingLines(planting, translate) {
  if (!planting) return [];
  const t = typeof translate === "function" ? translate : (value) => value;
  const lines = [];
  if (planting.cultivar) {
    lines.push(`${t("Cultivar")}: ${planting.cultivar}`);
  }
  if (planting.seedsThousandPerHa > 0) {
    lines.push(
      `${t("Recomendação de plantio")}: ${planting.seedsThousandPerHa.toLocaleString("pt-BR", { maximumFractionDigits: 1 })} ${t("mil sementes/ha")}`
    );
  }
  if (planting.pmsG > 0) {
    lines.push(
      `${t("PMS (peso de mil sementes)")}: ${planting.pmsG.toLocaleString("pt-BR", { maximumFractionDigits: 1 })} g`
    );
  }
  if (planting.rowSpacingCm > 0) {
    lines.push(
      `${t("Espaçamento entre linhas")}: ${planting.rowSpacingCm.toLocaleString("pt-BR", { maximumFractionDigits: 1 })} cm`
    );
  }
  const seedsPerMeter =
    planting.seedsPerMeter ||
    computeSeedsPerMeter({
      seedsThousandPerHa: planting.seedsThousandPerHa,
      rowSpacingCm: planting.rowSpacingCm,
    });
  if (seedsPerMeter > 0) {
    lines.push(
      `${t("Sementes por metro")}: ${seedsPerMeter.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}`
    );
  }
  if (planting.seedsPerPack > 0) {
    lines.push(
      `${t("Sementes por embalagem")}: ${planting.seedsPerPack.toLocaleString("pt-BR")} ${t("unidades")} (${t("bag/sacas")})`
    );
  }
  const supplyLabel = operatorSupplyUnitLabel(planting.operatorSupplyUnit, t);
  if (supplyLabel) {
    lines.push(`${t("Operador informa o abastecimento em")}: ${supplyLabel}`);
  }
  if (planting.seedSupplySeeds > 0) {
    const bags =
      planting.bagsRequired ||
      computeSeedBags({
        supplySeeds: planting.seedSupplySeeds,
        seedsPerPack: planting.seedsPerPack,
      });
    lines.push(
      `${t("Quantidade a abastecer")}: ${planting.seedSupplySeeds.toLocaleString("pt-BR")} ${t("sementes")}` +
        (bags > 0
          ? ` (${bags.toLocaleString("pt-BR", { maximumFractionDigits: 1 })} ${t("embalagens (bag/sacas)")})`
          : "")
    );
  }
  if (planting.fertilizerKgHa > 0) {
    lines.push(
      `${t("Adubo")}: ${planting.fertilizerKgHa.toLocaleString("pt-BR", { maximumFractionDigits: 1 })} kg/ha`
    );
  }
  if (planting.inoculantWaterLHa > 0) {
    lines.push(
      `${t("Inoculante")}: ${planting.inoculantWaterLHa.toLocaleString("pt-BR", { maximumFractionDigits: 1 })} L ${t("de água")}/ha`
    );
  }
  if (planting.binderRecommendation) {
    lines.push(
      `${t("Inoculante (recomendação)")}: ${planting.binderRecommendation}`
    );
  }
  return lines;
}
